import React, { useEffect, useRef, useState } from 'react';
import { Box, useToast, Text, Flex, CloseButton, useBreakpointValue } from '@chakra-ui/react';
import { FaComment, FaPlusCircle } from 'react-icons/fa';
import { CustomInputEmail } from '../molecules/ContactUsForm/CustomInputEmail';
import { CustomInputTextbox } from '../molecules/ContactUsForm/CustomInputTextbox';
import { CustomInputTel } from '../molecules/ContactUsForm/CustomInputTel';
import { CustomInputDate } from '../molecules/ContactUsForm/CustomInputDate';
import { CustomInputPassword } from '../molecules/ContactUsForm/CustomInputPassword';
import { CustomInputNumber } from '../molecules/ContactUsForm/CustomInputNumber';
import { CustomInputRadio } from '../molecules/ContactUsForm/CustomInputRadio';
import { CustomCheckbox } from '../atoms/CustomCheckbox';
import { CustomHeading } from '../atoms/CustomHeading';
import { CustomButton } from '../atoms';
import { FormField, useSettings } from '../../stateManagement/SettingsContext';
import {
  GlobalUserData,
  // hmac,
  initializeHelpshiftChat,
  listenToConversationStatus,
  log,
} from '../../utils';
import { appInsights } from '../../appInsights';
import { useAuth } from '../../stateManagement/auth/useAuth';
import { Loader } from './Loader';
import { CustomTextArea } from '../atoms/ContactUsForm/CustomTextArea';
import { JaggedBorderFooter } from '../atoms/JaggedBorderFooter';
// import { trackEvent } from '../../Analytics/ga';
import { JaggedBorderFooterMobile } from '../atoms/JaggedBorderFooterMobile';

interface ContactUsProps {
  accessToken: string | null;
  FormFields: FormField[] | null; // Array of form fields
  createTicketButtonText: string;
  liveChatButtonText: string;
  secondaryAccentColor?: string;
  showRocks?: boolean;
  showJaggedBorder?: boolean;
  footerBgColor?: string;
  rocksImageUrl?: string;
  contactUsDisclaimerToastTextColor?: string;
  contactUsDisclaimerToastBgColor?: string;
  contactUsDisclaimerToastText?: {
    activeIssue: string;
    noActiveIssue: string;
  };
  isPreviewMode?: boolean;
}

const PLATFORM_ID = process.env.REACT_APP_WEB_PLATFORM_ID || '';
const DOMAIN = process.env.REACT_APP_DOMAIN || '';
const LANGUAGE = process.env.REACT_APP_LANGUAGE || '';
// const SECRET_KEY = process.env.REACT_APP_SECRET_KEY || '';

export const ContactUs: React.FC<ContactUsProps> = ({
  accessToken,
  FormFields,
  createTicketButtonText,
  liveChatButtonText,
  secondaryAccentColor,
  showRocks,
  showJaggedBorder = true,
  footerBgColor,
  rocksImageUrl,
  contactUsDisclaimerToastTextColor = 'black',
  contactUsDisclaimerToastBgColor = 'white',
  contactUsDisclaimerToastText,
  isPreviewMode = false,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const { user } = useAuth();
  const userId: string = user?.idTokenClaims?.sub as string;
  const userEmail = user?.idTokenClaims?.emails?.[0] || '';
  const userName = user?.idTokenClaims?.name || '';
  // const steamId = user?.idTokenClaims?.steam_id as string;
  const [submitted, isSubmitted] = useState(false);
  const [disableUserName, setDisableUserName] = useState(false);
  const [loading, isLoading] = useState<Boolean>(false);
  const { settings } = useSettings();
  const globalUserData: GlobalUserData = {
    first_name: String(user?.idTokenClaims?.given_name || ''),
    last_name: String(user?.idTokenClaims?.family_name || ''),
    full_name: String(user?.idTokenClaims?.name || ''),
    display_name: String(user?.idTokenClaims?.name || ''),
    last_country: String(user?.idTokenClaims?.country || ''),
    last_city: String(user?.idTokenClaims?.city || ''),
    age: String(user?.idTokenClaims?.age || ''),
    lifetime_value: String(user?.idTokenClaims?.lifetimeValue || ''),
    user_persona: String(user?.idTokenClaims?.userPersona || ''),
    user_vip_segment: String(user?.idTokenClaims?.userVipSegment || ''),
    user_support_status: String(user?.idTokenClaims?.userSupportStatus || ''),
    last_active_date: String(user?.idTokenClaims?.lastActiveDate || ''),
    accepted_t_and_c: String(user?.idTokenClaims?.acceptedTAndC || false),
    preferred_language: String(user?.idTokenClaims?.preferredLanguage || ''),
  };
  const hasTrackedFormStart = useRef(false);
  const toast = useToast();
  const toastId = 'ticket-toast';

  const trackFormStartedOnce = () => {
    if (!hasTrackedFormStart.current) {
      // trackEvent('form_started', {
      //   page: 'contact_us',
      //   user_status: submitted ? 'submitted' : 'not_submitted',
      // });
      hasTrackedFormStart.current = true;
    }
  };

  // find name and email, set it in formdata, disable fields
  useEffect(() => {
    if (!user || !FormFields || !FormFields.length) return;

    // Find name/email fields robustly (fallback heuristics)
    const nameField =
      FormFields.find(f => f.id === '2') ||
      FormFields.find(f => (f.question || '').toLowerCase().includes('name'));

    const emailField =
      FormFields.find(f => f.type === 'email') ||
      FormFields.find(f => (f.question || '').toLowerCase().includes('email'));

    setFormData(prevData => ({
      ...prevData,
      [nameField?.id ?? 'name']: user?.idTokenClaims?.name || '',
      [emailField?.id ?? 'email']: user?.idTokenClaims?.emails?.[0] || '',
    }));
    hasTrackedFormStart.current = true;
    setDisableUserName(true);
  }, [user, FormFields]);

  const fetchData = async () => {
    isLoading(true);
    try {
      const status = await listenToConversationStatus();
      const activeIssues = status.open;
      // eslint-disable-next-line
      console.log('Activeissues', activeIssues);
      log(settings?.configs.showLogs, 'Activeissues', activeIssues);
      if (activeIssues) {
        isSubmitted(true);
        initializeHelpshiftChat({
          platformId: PLATFORM_ID,
          domain: DOMAIN,
          language: LANGUAGE,
          userName,
          userEmail,
          userId,
          // steam_id: steamId,
          globalUserData,
          accessToken,
          property: 'openWidget,showWidget',
          // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
        });
        if (!toast.isActive(toastId)) {
          toast({
            id: toastId,
            position: 'bottom-left',
            duration: 8000,
            isClosable: true,
            render: ({ onClose }) => (
              <Flex
                bg={contactUsDisclaimerToastBgColor}
                color={contactUsDisclaimerToastTextColor}
                px={4}
                py={3}
                my={4}
                borderRadius="lg"
                boxShadow="md"
                align="center"
                justify="space-between"
                minW="300px"
              >
                <Text fontSize="sm">
                  {contactUsDisclaimerToastText?.activeIssue ||
                    'A ticket is already active under your account. Please resolve your existing ticket to create a new one.'}
                </Text>
                <CloseButton size="sm" color={secondaryAccentColor} onClick={() => onClose()} />
              </Flex>
            ),
          });
        }
      } else {
        // eslint-disable-next-line
        if (!toast.isActive(toastId)) {
          toast({
            id: toastId,
            position: 'bottom-left',
            duration: 8000,
            isClosable: true,
            render: ({ onClose }) => (
              <Flex
                bg={contactUsDisclaimerToastBgColor}
                color={contactUsDisclaimerToastTextColor}
                px={4}
                py={3}
                my={4}
                borderRadius="lg"
                boxShadow="md"
                align="center"
                justify="space-between"
                minW="300px"
              >
                <Text fontSize="sm">
                  {contactUsDisclaimerToastText?.noActiveIssue ||
                    'Disclaimer: Only 1 ticket can be created at a time.'}
                </Text>
                <CloseButton size="sm" color={secondaryAccentColor} onClick={() => onClose()} />
              </Flex>
            ),
          });
        }
      }
    } catch (error) {
      // eslint-disable-next-line
      console.log('Error fetching data:', error);
    }
    isLoading(false);
  };

  // Ensure Helpshift initializes even after page refresh
  // needed when widget to be showed at all times irrespective of ticket existence
  useEffect(() => {
    // initializeHelpshiftChat({
    //   platformId: PLATFORM_ID,
    //   domain: DOMAIN,
    //   language: LANGUAGE,
    //   userName,
    //   userEmail,
    //   userId,
    //   steam_id: steamId,
    //   globalUserData,
    //   accessToken,
    //   property: 'closeWidget',
    //   userAuthToken: hmac(userId, userEmail, SECRET_KEY),
    // });
    fetchData();
  }, []);

  useEffect(() => {
    if (accessToken) {
      // eslint-disable-next-line
      // console.log('Access token is available:', accessToken);
      log(settings?.configs.showLogs, 'Access token is available:', accessToken);
      fetchData();
    }
  }, [accessToken]);

  // Collect all dependent child IDs for a given fieldId + selection
  const collectChildren = (
    fieldId: string,
    selection: string | undefined,
    fields: FormField[],
  ): string[] => {
    if (!selection) return [];

    // find the field object by id
    const field = fields.find(f => f.id === fieldId);
    if (!field || !field.options) return [];

    // get the children based on selected option
    const children = field.options[selection]?.question || [];
    let allChildren: string[] = [...children];

    // recursively collect children for each child
    children.forEach(childId => {
      const childValue = formData[childId];
      allChildren = [...allChildren, ...collectChildren(childId, childValue, fields)];
    });

    return allChildren;
  };
  const handleInputChange = (id: string, value: string) => {
    trackFormStartedOnce();

    setFormData(prevData => {
      const newData = { ...prevData, [id]: value };
      const childrenToClear = collectChildren(id, prevData[id], FormFields || []);

      childrenToClear.forEach(childId => {
        delete newData[childId];
      });
      return newData;
    });
  };
  const handleNumberChange = (question: string, newValue: number) => {
    trackFormStartedOnce();
    setFormData(prevData => ({
      ...prevData,
      [question]: newValue,
    }));
  };
  const handleLiveChatClick = () => {
    initializeHelpshiftChat({
      platformId: PLATFORM_ID,
      domain: DOMAIN,
      language: LANGUAGE,
      userName,
      userEmail,
      userId,
      // steam_id: steamId,
      globalUserData,
      accessToken,
      property: 'showWidget,openWidget',
      // userAuthToken: hmac(userId, userEmail, SECRET_KEY),
    });
    // trackEvent('live_chat_button_clicked', {
    //   page: 'contact_us',
    //   user_status: submitted ? 'submitted' : 'not_submitted',
    // });
  };

  const otherFields = FormFields?.filter(field => field.type !== 'heading') || [];
  const independentFields = otherFields?.filter(f => f.dependent === false);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    isSubmitted(true);

    // Build question-answer map from formData and FormFields
    const questionAnswerMap: Record<string, any> = {};
    FormFields?.forEach(field => {
      const answer = formData[field.id];
      if (answer !== undefined && answer !== null && answer !== '') {
        questionAnswerMap[field.question] = answer;
      }
    });
    // userFirstMessage from issueDetail/subType
    const subTypeField = FormFields?.find(item => item.id === 'q3');
    const subTypeFieldAns = subTypeField ? questionAnswerMap[subTypeField.question] : '';
    let userFirstMessage = subTypeFieldAns || '';
    // Find valid "Body" fields (Marked as UserFirstMessage + Actually Answered + Not q3)
    const answeredUserFirstMessageFields =
      FormFields?.filter(
        f =>
          f.isUserFirstMessage &&
          f.id !== 'q3' &&
          formData[f.id] !== undefined &&
          formData[f.id] !== null &&
          formData[f.id] !== '',
      ) || [];

    if (answeredUserFirstMessageFields.length > 0) {
      // find the first field that does not have a linked_cif_key
      const priorityField = answeredUserFirstMessageFields.find(f => !f.linked_cif_key);

      // use priority field if exists, otherwise take the first one
      const selectedField = priorityField || answeredUserFirstMessageFields[0];

      // extract the answer
      const messageBody = questionAnswerMap[selectedField.question] || '';

      // append with Newline
      if (messageBody) {
        userFirstMessage += userFirstMessage ? `\n${messageBody}` : messageBody;
      }
    }

    const cifDemo: Record<string, { type: string; value: any }> = {};
    FormFields?.forEach(field => {
      const key = field.linked_cif_key;
      const type = field.type || '';
      const value = formData[field.id];

      // include field if it has a linked_cif_key AND a value in formData
      if (key && value !== undefined && value !== null && value !== '') {
        if (type === 'dropdown') {
          const optionsArray = Object.keys(field.options || {});

          const selectedIndex = optionsArray.indexOf(value);

          if (selectedIndex !== -1) {
            cifDemo[key] = { type, value };
          }
        } else {
          cifDemo[key] = { type, value };
        }
      }
    });

    // const authToken = hmac(userId, userEmail, SECRET_KEY);

    initializeHelpshiftChat({
      platformId: PLATFORM_ID,
      domain: DOMAIN,
      language: LANGUAGE,
      userName,
      userEmail,
      userId,
      // steam_id: steamId,
      globalUserData,
      accessToken,
      property: 'showWidget,openWidget',
      // userAuthToken: authToken,
      userFirstMessage,
      customIssueFields: cifDemo,
    });

    appInsights.trackEvent({
      name: 'contactUsPage',
      properties: {
        buttonName: 'SubmitButton',
      },
    });

    // trackEvent('create_ticket_button_clicked', {
    //   page: 'contact_us',
    //   user_status: submitted ? 'submitted' : 'not_submitted',
    // });

    // eslint-disable-next-line
    // console.log('done', 'SubmitButton');
    log(settings?.configs.showLogs, 'done', 'SubmitButton');
  };

  const renderInputField = (field: any, index: number) => {
    const { id, type, question, description, placeholder, required, options, height } = field;

    const nameFieldId =
      FormFields?.find(f => f.id === '2')?.id ||
      FormFields?.find(f => (f.question || '').toLowerCase().includes('name'))?.id;

    const shouldDisable = id === nameFieldId && disableUserName;

    switch (type) {
      case 'heading':
        return (
          <Box key={id || index} marginBottom="2rem">
            <CustomHeading
              question={question}
              description={description}
              fontSize="4xl"
              descriptionFontSize="md"
              fontWeight="400"
              marginTop="2rem"
            />
          </Box>
        );
      case 'singleline':
        return (
          <CustomInputTextbox
            key={id}
            question={question}
            placeholder={placeholder}
            required={required}
            description={description}
            marginTop="2rem"
            fontSize="md"
            descriptionFontSize="sm"
            height={height || '50px'}
            onChange={e => handleInputChange(id, e.target.value)}
            value={formData[id] || ''}
            isDisabled={submitted || shouldDisable}
            id={id}
          />
        );
      case 'multiline':
        return (
          <CustomTextArea
            key={id}
            question={question}
            placeholder={placeholder}
            required={required}
            description={description}
            marginTop="2rem"
            fontSize="md"
            descriptionFontSize="sm"
            height={height || '200px'}
            onChange={e => handleInputChange(id, e.target.value)}
            value={formData[id] || ''}
            isDisabled={submitted || shouldDisable}
          />
        );
      case 'password':
        return (
          // <div key={index} style={{ flex: '1 1 45%', margin: '0.5rem' }}>
          <CustomInputPassword
            key={id}
            question={question}
            placeholder={placeholder}
            required={required}
            description={description}
            marginTop="2rem"
            fontSize="md"
            descriptionFontSize="sm"
            onChange={e => handleInputChange(id, e.target.value)}
            value={formData[id] || ''}
            isDisabled={submitted}
          />
          // </div>
        );
      case 'email':
        return (
          // <div key={index} style={{ flex: '1 1 45%', margin: '0.5rem' }}>
          <CustomInputEmail
            key={id}
            question={question}
            placeholder={placeholder}
            required={required}
            description={description}
            marginTop="2rem"
            fontSize="md"
            descriptionFontSize="sm"
            onChange={e => handleInputChange(id, e.target.value)}
            value={formData[id] || ''}
            isDisabled={submitted}
          />
          // </div>
        );
      case 'date':
        return (
          <div key={index} style={{ flex: '1 1 45%', margin: '0.5rem' }}>
            <CustomInputDate
              question={question}
              placeholder={placeholder}
              required={required}
              description={description}
              marginTop="2rem"
              fontSize="md"
              descriptionFontSize="sm"
              onChange={e => handleInputChange(id, e.target.value)}
              value={formData[id] || ''}
              isDisabled={submitted}
            />
          </div>
        );
      case 'tel':
        return (
          <div key={index} style={{ flex: '1 1 45%', margin: '0.5rem' }}>
            <CustomInputTel
              question={question}
              placeholder={placeholder}
              required={required}
              description={description}
              marginTop="2rem"
              fontSize="md"
              descriptionFontSize="sm"
              onChange={e => handleInputChange(id, e.target.value)}
              value={formData[id] || ''}
              isDisabled={submitted}
            />
          </div>
        );
      case 'number':
        return (
          <div key={index} style={{ flex: '1 1 45%', margin: '0.5rem' }}>
            <CustomInputNumber
              question={question}
              placeholder={placeholder}
              required={required}
              description={description}
              marginTop="2rem"
              fontSize="md"
              descriptionFontSize="sm"
              isDisabled={submitted}
              onChange={(value: number) => handleNumberChange(id, value)}
              value={formData[id] || ''}
            />
          </div>
        );
      case 'checkbox':
        return (
          <div style={{ flex: '1 1 45%', margin: '0.5rem' }}>
            <CustomCheckbox
              question={question}
              isRequired={required}
              marginTop="2rem"
              onChange={e => handleInputChange(id, e.target.value)}
              value={formData[id] || ''}
              isDisabled={submitted}
            />
          </div>
        );

      case 'dropdown': {
        let filteredOptions = options;

        if (options) {
          filteredOptions = Object.fromEntries(
            Object.entries(options).filter(([, config]: any) => {
              if (!config.showIf) return true; // independent option → always show
              const parentAnswers = Object.values(formData);
              return parentAnswers.some(answer => config.showIf.includes(answer));
            }),
          );
        }
        if (Object.keys(filteredOptions).length === 0) {
          return null;
        }

        return (
          // <div style={{ flex: '1 1 45%', margin: '0.5rem' }}>
          <CustomInputRadio
            key={id}
            question={question}
            placeholder={placeholder}
            required={required}
            description={description}
            options={Object.keys(filteredOptions)}
            marginTop="2rem"
            fontSize="md"
            descriptionFontSize="sm"
            onChange={val => handleInputChange(id, val)}
            value={formData[id]}
            isDisabled={submitted}
          />
          // </div>
        );
      }
      case 'information':
        return (
          <CustomHeading
            key={index}
            question={question}
            description={description}
            fontSize="lg"
            descriptionFontSize="md"
            descriptionColor="black"
            textAlign="left"
            marginTop="2rem"
            fontWeight="bold"
          />
        );
      default:
        return null;
    }
  };

  // Get children IDs for a field based on selection
  const getChildrenForSelection = (
    fieldId: string,
    selection: string,
    fields: FormField[],
  ): string[] => {
    const field = fields.find(f => f.id === fieldId);
    if (!field || !field.options) return [];

    const children = field.options[selection]?.question || [];
    return children;
  };

  const renderChain = (fieldId: string): React.ReactNode => {
    const field = FormFields?.find(f => f.id === fieldId);
    if (!field) return null;

    const fieldObj = { ...field, id: field.id ?? fieldId };

    const node = (
      <Box key={fieldId} marginBottom="1rem">
        {renderInputField(fieldObj, 0)}
      </Box>
    );

    const selected = formData[fieldObj.id];
    if (!selected) return node;

    const children = getChildrenForSelection(fieldObj.id, selected, FormFields || []);

    if (!children.length) return node;

    return (
      <>
        {node}
        {children.map(childId => renderChain(childId))}
      </>
    );
  };

  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Box>
      {loading ? (
        <Loader />
      ) : (
        <>
          {/* Main Form Wrapper */}
          <form onSubmit={handleSubmit}>
            {/* Form Content Section */}
            <Box padding="2rem" maxWidth="1200px" margin="0 auto">
              <Box display="flex" flexDirection="column" gap="1rem">
                {independentFields?.map((field, index) => (
                  <Box key={field.id || index} marginBottom="1rem">
                    {renderInputField(field, index)}
                  </Box>
                ))}
                {(() => {
                  const firstDependentId = FormFields?.find(f => f.dependent === true)?.id;
                  return firstDependentId ? renderChain(firstDependentId) : null;
                })()}
              </Box>
            </Box>

            {/* Footer with BG image and buttons */}
            <Box
              width="100%"
              backgroundImage={showRocks ? `url(${rocksImageUrl})` : undefined}
              backgroundRepeat="no-repeat"
              backgroundPosition={{
                base: 'center bottom',
                sm: 'center calc(100% - 50px)',
                md: 'center calc(100% - 60px)',
                lg: 'center calc(100% - 20px)',
              }}
              backgroundSize="100% auto"
              height="420px"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              position="relative"
            >
              <Box
                width="100%"
                maxWidth="1200px"
                margin="0 auto"
                padding="2rem"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                zIndex={1}
                position="relative"
              >
                <CustomButton
                  type="submit"
                  variant="solid"
                  size="lg"
                  textColor="white"
                  backgroundColor={secondaryAccentColor}
                  padding="12px 24px"
                  icon={<FaPlusCircle />}
                  borderRadius="0"
                  style={{ marginBottom: '16px', width: '100%' }}
                  isDisabled={submitted || isPreviewMode}
                >
                  {createTicketButtonText}
                </CustomButton>
                <CustomButton
                  onClick={handleLiveChatClick}
                  variant="solid"
                  size="lg"
                  textColor={secondaryAccentColor}
                  border="1px solid"
                  borderColor={secondaryAccentColor}
                  borderRadius="0"
                  backgroundColor="white"
                  padding="12px 24px"
                  icon={<FaComment />}
                  style={{ marginBottom: '16px', width: '100%' }}
                  isDisabled={!submitted || isPreviewMode}
                >
                  {liveChatButtonText}
                </CustomButton>
              </Box>

              {/* Jagged border positioned at bottom */}
              {showJaggedBorder &&
                (isMobile ? (
                  <Box position="absolute" bottom="0" width="100%" zIndex="0">
                    <JaggedBorderFooterMobile bgColor={footerBgColor} />
                  </Box>
                ) : (
                  <Box position="absolute" bottom="0" width="100%" zIndex="0">
                    <JaggedBorderFooter bgColor={footerBgColor} />
                  </Box>
                ))}
            </Box>
          </form>
        </>
      )}
    </Box>
  );
};
